import type { SearchScope } from "@/features/search/searchScope";
import type {
  EventDispatchResult,
  TypedEvent,
} from "@/shared/lib/componentHierarchy";

export type ArchiveEvent =
  | { readonly type: "archive/search-draft-changed"; readonly value: string }
  | {
      readonly type: "archive/search-query-synchronized";
      readonly query: string;
    }
  | { readonly type: "archive/search-submission-requested" }
  | { readonly type: "archive/search-clear-requested" }
  | {
      readonly type: "archive/search-scope-change-requested";
      readonly scope: SearchScope;
    }
  | { readonly type: "archive/search-committed"; readonly query: string }
  | { readonly type: "archive/search-cleared" }
  | {
      readonly type: "archive/search-scope-changed";
      readonly scope: SearchScope;
    }
  | { readonly type: "archive/page-change-requested"; readonly page: number }
  | { readonly type: "archive/page-redirect-requested"; readonly page: number }
  | { readonly type: "archive/refresh-requested" }
  | { readonly type: "archive/retry-requested" }
  | { readonly type: "archive/storage-disclosure-toggle-requested" }
  | { readonly type: "archive/sidebar-toggle-requested" }
  | {
      readonly type: "archive/scroll-requested";
      readonly edge: "top" | "bottom";
    }
  | {
      readonly type: "archive/settings-link-clicked";
      readonly hasModifier: boolean;
    }
  | { readonly type: "archive/settings-close-requested" }
  | {
      readonly type: "archive/viewport-synchronized";
      readonly isDesktop: boolean;
    }
  | { readonly type: "archive/item-reveal-requested" }
  | { readonly type: "archive/item-synchronized"; readonly threadId: string };

const ARCHIVE_EVENT_TYPES = new Set<ArchiveEvent["type"]>([
  "archive/search-draft-changed",
  "archive/search-query-synchronized",
  "archive/search-submission-requested",
  "archive/search-clear-requested",
  "archive/search-scope-change-requested",
  "archive/search-committed",
  "archive/search-cleared",
  "archive/search-scope-changed",
  "archive/page-change-requested",
  "archive/page-redirect-requested",
  "archive/refresh-requested",
  "archive/retry-requested",
  "archive/storage-disclosure-toggle-requested",
  "archive/sidebar-toggle-requested",
  "archive/scroll-requested",
  "archive/settings-link-clicked",
  "archive/settings-close-requested",
  "archive/viewport-synchronized",
  "archive/item-reveal-requested",
  "archive/item-synchronized",
]);

export function isArchiveEvent(event: TypedEvent): event is ArchiveEvent {
  return ARCHIVE_EVENT_TYPES.has(event.type as ArchiveEvent["type"]);
}

export type ArchiveEventNotifier = (
  event: ArchiveEvent,
) => EventDispatchResult<TypedEvent>["status"];
